import React, { useState } from "react";
import { useHistory } from "react-router";
import styled, { css, keyframes } from "styled-components";
import { C, F, EASE, ring } from "../styles";
import { MATERIALS, DEFAULT_MATERIAL_URL, THUMBS, WEEKS } from "../data/transmissao";
import { currentPosition, dayState, materialAt, slotStart, slotState, unlockAt } from "../utils/schedule";
import { LESSON_MINUTES } from "../data/transmissao";
import iconeTv from "../assets/transmissao/icone-tv.svg";

export const CARD_GRAD = "linear-gradient(135deg,rgba(46,169,222,0.32) 0%,rgba(46,169,222,0.08) 55%,rgba(11,16,102,0.35) 100%),#1B229F";
export const PANEL_GRAD = "linear-gradient(135deg,rgba(46,169,222,0.22) 0%,rgba(46,169,222,0.06) 55%,rgba(11,16,102,0.35) 100%),#1B229F";
const PANEL_RING = ring(1, "rgba(255,255,255,0.14)");
const HOT_RING = `${ring(1.5, C.yellow)}, 0 0 0 4px rgba(255,255,0,0.12)`;

const blink = keyframes`
  from { opacity: 1; }
  to { opacity: 0.25; }
`;

export const blinking = css`
  animation: ${blink} 1s ease-in-out infinite alternate;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const boil = keyframes`
  from { transform: rotate(-10deg); }
  to { transform: rotate(-2deg); }
`;

export const Dot = styled.span`
  flex: none;
  width: ${(p) => p.size || 8}px;
  height: ${(p) => p.size || 8}px;
  border-radius: 50%;
  background: ${(p) => p.color || C.orange};
  ${(p) => p.blink && blinking}
`;

const Wrap = styled.section`
  scroll-margin-top: 16px;
  position: relative;
  max-width: 1096px;
  margin: 0 auto;
  padding: clamp(48px, 7vw, 88px) clamp(16px, 4vw, 48px);
  display: flex;
  flex-direction: column;
  gap: clamp(28px, 3.6vw, 40px);
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  text-align: center;
  h2 {
    margin: 0;
    font: 700 clamp(28px, 3vw, 40px) / 1.1 ${F.title};
    letter-spacing: -0.04em;
    text-wrap: balance;
  }
  h2 img {
    display: inline-block;
    vertical-align: middle;
    margin-left: clamp(8px, 1vw, 14px);
    width: clamp(34px, 3.4vw, 48px);
    height: auto;
    transform: rotate(-6deg);
    animation: ${boil} 4.2s ease-in-out infinite alternate;
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
  p {
    margin: 0;
    font-size: clamp(16px, 1.5vw, 18px);
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.85);
    text-wrap: pretty;
  }
`;

export const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  > span:first-child {
    font: 700 clamp(20px, 2vw, 24px) / 1.2 ${F.title};
    letter-spacing: -0.03em;
    white-space: nowrap;
  }
  > i {
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.14);
  }
`;

const Panel = styled.div`
  border-radius: 20px;
  overflow: hidden;
  background: ${PANEL_GRAD};
  box-shadow: ${PANEL_RING};
`;

const WeekTabs = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  padding: 6px;
  margin: 12px 12px 0;
  border-radius: 14px;
  background: rgba(7, 10, 51, 0.82);
  box-shadow: ${ring(1, "rgba(255,255,255,0.1)")};
`;

const WeekTab = styled.button`
  border: none;
  cursor: pointer;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  text-align: center;
  background: ${(p) => (p.$on ? C.blue : "transparent")};
  transition: background 200ms;
  small {
    font: 700 11px/1 ${F.title};
    letter-spacing: 0.1em;
    color: ${(p) => (p.$on ? C.yellow : "rgba(255,255,255,0.5)")};
  }
  span {
    font: 700 clamp(13px, 1.3vw, 15px) / 1.25 ${F.title};
    color: ${(p) => (p.$on ? C.white : "rgba(255,255,255,0.7)")};
  }
`;

const Inner = styled.div`
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const DayTabs = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
`;

const DayTab = styled.button`
  border: none;
  cursor: pointer;
  border-radius: 10px;
  padding: 10px 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: ${(p) => (p.$on ? C.yellow : "rgba(255,255,255,0.05)")};
  small {
    font: 600 10px/1 ${F.title};
    letter-spacing: 0.1em;
    color: ${(p) => (p.$on ? C.blue : "rgba(255,255,255,0.6)")};
  }
  span {
    font: 400 17px/1 ${F.num};
    color: ${(p) => (p.$on ? C.blue : C.white)};
  }
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols}, minmax(0, 1fr));
  gap: 18px;
`;

const DayCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding: 14px;
  border-radius: 16px;
  overflow: hidden;
  background: ${CARD_GRAD};
  box-shadow: ${(p) => (p.$today ? HOT_RING : PANEL_RING)};
  transition: transform 240ms ${EASE}, box-shadow 240ms;
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${HOT_RING};
  }
  .dim {
    opacity: ${(p) => (p.$past ? 0.45 : 1)};
    transition: opacity 240ms;
  }
  &:hover .dim,
  &:hover .past-time {
    opacity: 1;
  }
  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }
`;

const Thumb = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  overflow: hidden;
  background: ${C.ink};
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.18) 0 1px, transparent 1px 3px);
    mix-blend-mode: multiply;
  }
`;

const ThumbEmpty = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.04) 0 1px, transparent 1px 4px), ${C.ink};
  span {
    font: 400 clamp(26px, 3vw, 34px) / 1 ${F.num};
    color: rgba(255, 255, 255, 0.18);
    letter-spacing: 0.04em;
  }
`;

const DayHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 4px 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  small {
    font: 600 11px/1 ${F.title};
    letter-spacing: 0.12em;
    color: ${C.light};
  }
  b {
    font: 400 22px/1 ${F.num};
    letter-spacing: 0.02em;
    color: ${(p) => (p.$today ? C.yellow : C.white)};
    font-weight: 400;
  }
`;

const TodayTag = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 20px;
  background: rgba(7, 10, 51, 0.82);
  box-shadow: ${ring(1, "rgba(255,255,0,0.6)")};
  font: 600 11px/1 ${F.title};
  letter-spacing: 0.06em;
  color: ${C.yellow};
  white-space: nowrap;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.07);
  > b {
    flex: none;
    width: 44px;
    font: 400 16px/1 ${F.num};
    letter-spacing: 0.02em;
    color: ${C.yellow};
    font-variant-numeric: tabular-nums;
    font-weight: 400;
    opacity: ${(p) => (p.$past ? 0.6 : 1)};
    transition: opacity 240ms;
  }
`;

const Who = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  strong {
    font: 700 14.5px/1.2 ${F.title};
    color: ${(p) => (p.$guest ? C.yellow : C.white)};
  }
  span {
    font: 500 12.5px/1.3 ${F.body};
    color: ${C.light};
  }
`;

const LivePill = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 9px;
  border-radius: 20px;
  background: ${C.orange};
  font: 700 10.5px/1 ${F.title};
  letter-spacing: 0.08em;
  color: ${C.white};
`;

const PastLabel = styled.span`
  flex: none;
  font: 600 10.5px/1 ${F.title};
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.6);
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 0 0;
`;

const actionBase = css`
  flex: 1 1 auto;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 12px;
  border-radius: 10px;
  text-decoration: none;
`;

const Watch = styled.button`
  ${actionBase}
  border: none;
  cursor: pointer;
  background: ${C.yellow};
  color: ${C.blue};
  font: 700 12.5px/1.2 ${F.title};
  transition: filter 200ms;
  &:hover {
    filter: brightness(1.08);
  }
`;

const Download = styled.a`
  ${actionBase}
  background: rgba(7, 10, 51, 0.82);
  box-shadow: ${ring(1, "rgba(255,255,255,0.2)")};
  color: ${C.white};
  font: 600 13px/1 ${F.title};
  &:hover {
    color: ${C.white};
    box-shadow: ${ring(1, C.yellow)};
  }
`;

const Locked = styled.span`
  ${actionBase}
  background: rgba(7, 10, 51, 0.5);
  box-shadow: ${ring(1, "rgba(255,255,255,0.1)")};
  color: rgba(255, 255, 255, 0.55);
  font: 600 12.5px/1.2 ${F.title};
  cursor: not-allowed;
`;

const Saturday = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-radius: 16px;
  background: rgba(7, 10, 51, 0.35);
  box-shadow: ${ring(1.5, C.yellow)}, 0 0 0 4px rgba(255, 255, 0, 0.1);
`;

const SatHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 0 4px 10px;
  border-bottom: 1px solid rgba(255, 255, 0, 0.25);
  > b {
    font: 400 30px/1 ${F.num};
    letter-spacing: 0.02em;
    color: ${C.yellow};
    font-weight: 400;
  }
  > em {
    margin-left: auto;
    font: 600 13px/1.3 ${F.title};
    font-style: normal;
    color: ${C.white};
  }
`;

const SatTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 15px;
  border-radius: 20px;
  background: rgba(7, 10, 51, 0.82);
  box-shadow: ${ring(1, "rgba(255,255,0,0.6)")};
  font: 600 13px/1.25 ${F.title};
  color: ${C.yellow};
`;

const SatToday = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 20px;
  background: ${C.yellow};
  font: 700 11px/1 ${F.title};
  letter-spacing: 0.06em;
  color: ${C.blue};
  white-space: nowrap;
`;

const SatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  gap: 8px;
`;

const Sims = styled.div`
  margin-top: 6px;
  padding: clamp(18px, 2.4vw, 24px);
  border-radius: 16px;
  background: rgba(7, 10, 51, 0.35);
  box-shadow: ${ring(1.5, C.light)}, 0 0 0 4px rgba(108, 203, 239, 0.1);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: clamp(16px, 2.4vw, 28px);
`;

const SimsIntro = styled.div`
  flex: 1 1 240px;
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  > span {
    flex: none;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${C.light};
    display: flex;
    align-items: center;
    justify-content: center;
  }
  small {
    display: block;
    font: 600 12px/1 ${F.title};
    letter-spacing: 0.1em;
    color: ${C.light};
    margin-bottom: 4px;
  }
  strong {
    font: 700 clamp(17px, 1.7vw, 20px) / 1.2 ${F.title};
    letter-spacing: -0.02em;
    color: ${C.white};
  }
`;

const SimsList = styled.div`
  flex: 2 1 360px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 170px), 1fr));
  gap: 10px;
  > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 14px;
    border-radius: 12px;
    background: rgba(7, 10, 51, 0.55);
    box-shadow: ${ring(1, "rgba(255,255,255,0.1)")};
  }
  strong {
    font: 700 14.5px/1.2 ${F.title};
    color: ${C.white};
  }
  span {
    font: 500 12.5px/1.3 ${F.body};
    color: ${C.light};
  }
`;

const SimsCta = styled.a`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 22px;
  border-radius: 12px;
  background: ${C.orange};
  color: ${C.white};
  text-decoration: none;
  font: 700 15px/1 ${F.title};
  white-space: nowrap;
  transition: background 200ms;
  &:hover {
    color: ${C.white};
    background: ${C.orangeHover};
  }
`;

export const IconPlay = ({ color = C.blue, size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 4v16l14-8z" fill={color} />
  </svg>
);

export const IconClipboard = ({ color = C.yellow }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2 M9 14l2 2 4-4" />
  </svg>
);

const IconLock = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

export const materialUrl = (date) => MATERIALS[date] || DEFAULT_MATERIAL_URL || "";

function SlotRow({ day, slot, now }) {
  const [time, prof, disc, guest] = slot;
  const start = slotStart(day.date, time);
  const { isLive, isPast } = slotState({ start, end: start + LESSON_MINUTES * 60000 }, now);
  return (
    <Row $past={isPast}>
      <b className={isPast ? "past-time" : undefined}>{time}</b>
      <Who $guest={guest}>
        <strong>{prof}</strong>
        <span>{disc}</span>
      </Who>
      {isLive && (
        <LivePill>
          <Dot size={6} color={C.white} blink />
          NO AR
        </LivePill>
      )}
      {isPast && <PastLabel>JÁ ROLOU</PastLabel>}
    </Row>
  );
}

function DayActions({ day, now, onWatch }) {
  const mat = materialUrl(day.date);
  if (now < materialAt(day) || (now < unlockAt(day) && !mat)) {
    return (
      <Actions>
        <Locked>
          <IconLock />
          Libera após o aulão
        </Locked>
      </Actions>
    );
  }
  // Durante os aulões do dia: só o material (a gravação vem no fim).
  if (now < unlockAt(day)) {
    return (
      <Actions>
        <Download href={mat} target="_blank" rel="noopener noreferrer">
          Baixar material
        </Download>
      </Actions>
    );
  }
  return (
    <Actions>
      <Watch type="button" onClick={() => onWatch(day)}>
        <IconPlay />
        Assistir aulas
      </Watch>
      {mat && (
        <Download href={mat} target="_blank" rel="noopener noreferrer">
          Baixar material
        </Download>
      )}
    </Actions>
  );
}

function DayColumn({ day, now, onWatch }) {
  const { past, today } = dayState(day, now);
  const thumb = THUMBS[day.date];
  return (
    <DayCard $past={past} $today={today}>
      <Thumb>
        {thumb ? <img src={thumb} alt={`Professores do aulão de ${day.label}`} /> : <ThumbEmpty><span>{day.label}</span></ThumbEmpty>}
      </Thumb>
      <DayHead className="dim" $today={today}>
        <small>{day.dow}</small>
        <b>{day.label}</b>
        {today && (
          <TodayTag>
            <Dot size={6} blink />
            HOJE
          </TodayTag>
        )}
      </DayHead>
      {day.slots.map((s) => (
        <SlotRow key={s[0]} day={day} slot={s} now={now} />
      ))}
      <DayActions day={day} now={now} onWatch={onWatch} />
    </DayCard>
  );
}

function SaturdayBlock({ day, now, onWatch }) {
  const { today } = dayState(day, now);
  return (
    <Saturday>
      <SatHead>
        <SatTag>
          <Dot blink />
          SÁBADO ESPECIAL
        </SatTag>
        <b>{day.label}</b>
        <em>Maratona com os convidados</em>
        {today && <SatToday>HOJE</SatToday>}
      </SatHead>
      <SatGrid>
        {day.slots.map((s) => (
          <SlotRow key={s[0]} day={day} slot={s} now={now} />
        ))}
      </SatGrid>
      <DayActions day={day} now={now} onWatch={onWatch} />
    </Saturday>
  );
}

export default function Programacao({ now, mobile, onWatch }) {
  const history = useHistory();
  const [pos, setPos] = useState(() => currentPosition(now));
  const week = WEEKS[pos.week];
  const weekdays = week.days.slice(0, 3);
  const saturday = week.days[3];
  const showSat = !mobile || pos.day === 3;
  const columns = mobile ? (pos.day < 3 ? [weekdays[pos.day]] : []) : weekdays;

  return (
    <Wrap id="programacao">
      <Intro>
        <h2>
          A programação do SOS
          <img src={iconeTv} alt="" aria-hidden="true" />
        </h2>
        <p>Perdeu algum aulão? Assiste de novo, baixa o material do dia e testa o que aprendeu nos simulados.</p>
      </Intro>

      <Section>
        <SectionTitle>
          <span>Programação</span>
          <i />
        </SectionTitle>
        <Panel>
          <WeekTabs role="tablist">
            {WEEKS.map((w, i) => (
              <WeekTab key={w.num} type="button" role="tab" aria-selected={i === pos.week} $on={i === pos.week} onClick={() => setPos({ week: i, day: 0 })}>
                <small>{w.num}</small>
                <span>{w.short}</span>
              </WeekTab>
            ))}
          </WeekTabs>
          <Inner>
            {mobile && (
              <DayTabs role="tablist">
                {week.days.map((d, i) => (
                  <DayTab key={d.date} type="button" role="tab" aria-selected={i === pos.day} $on={i === pos.day} onClick={() => setPos({ ...pos, day: i })}>
                    <small>{d.dow}</small>
                    <span>{d.label}</span>
                  </DayTab>
                ))}
              </DayTabs>
            )}
            {columns.length > 0 && (
              <Columns $cols={mobile ? 1 : 3}>
                {columns.map((d) => (
                  <DayColumn key={d.date} day={d} now={now} onWatch={onWatch} />
                ))}
              </Columns>
            )}
            {showSat && <SaturdayBlock day={saturday} now={now} onWatch={onWatch} />}
            <Sims>
              <SimsIntro>
                <span>
                  <IconClipboard color={C.navy} />
                </span>
                <div>
                  <small>SIMULADOS DA SEMANA</small>
                  <strong>Teste o que você revisou</strong>
                </div>
              </SimsIntro>
              <SimsList>
                {week.simulados.map((m) => (
                  <div key={m.area}>
                    <strong>{m.area}</strong>
                    <span>
                      Libera {m.open} às 00h · até {m.close}
                    </span>
                  </div>
                ))}
              </SimsList>
              <SimsCta
                href="/simulados"
                onClick={(e) => {
                  e.preventDefault();
                  history.push("/simulados");
                }}
              >
                Ir para os simulados&nbsp;→
              </SimsCta>
            </Sims>
          </Inner>
        </Panel>
      </Section>
    </Wrap>
  );
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
